// @upryzze/forms — safe-expression engine: limits, function whitelist,
// reserved names. (PR 1 of the decision-tool epic, docs/design/
// decision-tool-forms.md §5.2.)
//
// This engine powers computed fields (the "calculator") and conditional
// visibility (`visibleWhen`). Tenant- and AI-authored strings flow into it,
// so it is a parse→AST→evaluate interpreter, NEVER `eval`/`new Function`.
// The grammar has no member-access operator at all, which makes prototype
// access (`__proto__`/`constructor`) structurally impossible to express;
// the reserved-name set below is a second, defensive layer.

export const EXPR_LIMITS = Object.freeze({
  // Reject pathological inputs cheaply, before/while parsing.
  MAX_SOURCE_LENGTH: 1000,
  MAX_NODES: 256,
  MAX_DEPTH: 32,
});

// Pure, side-effect-free functions the language exposes. `minArgs`/`maxArgs`
// are validated statically (arity). For data accessors:
//   - `literalArgs` = positions that MUST be string literals (never computed),
//     so every name the accessor uses is static and auditable — this keeps the
//     grammar free of computed property names reaching the host resolver.
//   - `fieldArgs` ⊆ `literalArgs` = positions that must reference a real form
//     field/group (checked against the field set). The non-field literal args
//     (e.g. a catalog attribute name, a repeatable child name) are not form
//     fields, so they're only required to be literal + non-reserved.
export const EXPR_FUNCTIONS = Object.freeze({
  // ── math ──
  min: { minArgs: 1, maxArgs: 64 },
  max: { minArgs: 1, maxArgs: 64 },
  abs: { minArgs: 1, maxArgs: 1 },
  round: { minArgs: 1, maxArgs: 2 }, // round(x) | round(x, digits)
  ceil: { minArgs: 1, maxArgs: 1 },
  floor: { minArgs: 1, maxArgs: 1 },
  clamp: { minArgs: 3, maxArgs: 3 }, // clamp(x, lo, hi)
  // ── data accessors (all name args = string literals) ──
  // attr('field', 'attrName'): field is a form field; attrName is a catalog key
  attr: { minArgs: 2, maxArgs: 2, literalArgs: [0, 1], fieldArgs: [0] },
  // count('group'): group is a repeatable field
  count: { minArgs: 1, maxArgs: 1, literalArgs: [0], fieldArgs: [0] },
  // sum('group', 'child'): group is a repeatable field; child is a row key
  sum: { minArgs: 2, maxArgs: 2, literalArgs: [0, 1], fieldArgs: [0] },
});

// Identifiers that must never be usable as a field reference, even though the
// grammar already blocks member access. Mirrors compile.js's
// RESERVED_FIELD_NAME_SEGMENTS (5.5b prototype-pollution guard).
export const RESERVED_IDENTIFIERS = Object.freeze(
  new Set(["__proto__", "prototype", "constructor"]),
);

// Error codes (SCREAMING_SNAKE, matching compile.js's convention).
export const EXPR_ERROR = Object.freeze({
  TOO_LONG: "EXPR_TOO_LONG",
  TOO_LARGE: "EXPR_TOO_LARGE",
  TOO_DEEP: "EXPR_TOO_DEEP",
  SYNTAX: "EXPR_SYNTAX_ERROR",
  EMPTY: "EXPR_EMPTY",
  UNKNOWN_FIELD: "EXPR_UNKNOWN_FIELD",
  RESERVED_IDENTIFIER: "EXPR_RESERVED_IDENTIFIER",
  UNKNOWN_FUNCTION: "EXPR_UNKNOWN_FUNCTION",
  BAD_ARITY: "EXPR_BAD_ARITY",
  DATA_ARG_NOT_LITERAL: "EXPR_DATA_ARG_NOT_LITERAL",
});
